import {
	createApi,
	fetchBaseQuery,
	QueryReturnValue,
} from '@reduxjs/toolkit/query/react'
import StorageService, { StorageKeys } from '../../services/StorageService'
import {
	AskQuestionReq,
	Candidate,
	ClarificationDetailRes,
	ClarificationRes,
	LoginReq,
	LoginRes,
	MatchedCandidatesRes,
	ParseVacancyReq,
	PostVacancyReq,
	ShortVacancy,
	Vacancy,
} from '../../types'
import { logout } from '../slice/appSlice'
import { isApiResponse } from '../../services/isApiError'

const baseQuery = fetchBaseQuery({
	baseUrl: process.env.REACT_APP_API_URL,
	prepareHeaders: (headers) => {
		const getToken = () => {
			try {
				const token = StorageService.getItem(StorageKeys.access_token)
				if (token) {
					headers.set('Authorization', `Bearer ${token}`)
				}
			} catch (e) {
				console.error(e)
			}
		}
		getToken()
		return headers
	},
})

const baseQueryWithReauth = async (args, api, extraOptions) => {
	const result = await baseQuery(args, api, extraOptions)
	if (
		result.error &&
		isApiResponse(result.error) &&
		result.error.data.code === 'token_not_valid'
	) {

		const refreshResult = await fetch(
			`${process.env.REACT_APP_API_URL}/token/refresh/`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					refresh: StorageService.getItem(StorageKeys.refresh_token),
				}),
			},
		).then((response) => response.json())
		console.log('refreshResult', refreshResult)
		if (refreshResult) {
			const res = refreshResult as LoginRes
			StorageService.setItem(StorageKeys.access_token, res.access)
			StorageService.setItem(StorageKeys.refresh_token, res.refresh)
			return await baseQuery(args, api, extraOptions)
		} else {
			api.dispatch(logout())
		}
	} else {
		return result
	}
}

export const vacanciesApi = createApi({
	reducerPath: 'vacanciesApi',
	baseQuery: baseQueryWithReauth,
	tagTypes: ['Vacancies', 'Candidates', 'Questions'],
	endpoints: (build) => ({
		login: build.mutation<LoginRes, LoginReq>({
			query: (body) => ({
				method: 'POST',
				url: '/login/',
				body,
			}),
		}),
		getVacancies: build.query<ShortVacancy[], void>({
			query: () => ({
				url: '/vacancies/',
			}),
			providesTags: ['Vacancies'],
		}),
		getVacancy: build.query<Vacancy, string>({
			query: (id) => ({
				url: `/vacancies/${id}/`,
			}),
			providesTags: ['Vacancies'],
		}),
		postVacancy: build.mutation<void, PostVacancyReq>({
			query: (body) => ({
				method: 'POST',
				url: '/vacancies/',
				body,
			}),
			invalidatesTags: ['Vacancies'],
		}),
		deleteVacancy: build.mutation<void, string>({
			query: (id) => ({
				method: 'DELETE',
				url: `/vacancies/${id}/`,
			}),
			invalidatesTags: ['Vacancies'],
		}),
		parseVacancy: build.mutation<void, ParseVacancyReq>({
			query: (body) => ({
				method: 'POST',
				url: '/vacancies/parse/',
				body,
			}),
			invalidatesTags: ['Vacancies'],
		}),
		putVacancy: build.mutation<void, { id: string; body: Vacancy }>({
			query: ({ id, body }) => ({
				method: 'PUT',
				url: `/vacancies/${id}/`,
				body,
			}),
			invalidatesTags: ['Vacancies'],
		}),
		getMatchedCandidates: build.query<
			MatchedCandidatesRes,
			{ id: string; page: number; page_size: number }
		>({
			query: ({ id, page, page_size }) => ({
				url: `/vacancies/${id}/matched-candidates/`,
				params: { page, page_size },
			}),
			providesTags: ['Candidates'],
		}),
		searchCandidates: build.mutation<void, string>({
			query: (id) => ({
				method: 'GET',
				url: `/vacancies/${id}/search-candidates/`,
			}),
			invalidatesTags: ['Candidates'],
		}),
		getCandidate: build.query<Candidate, string>({
			query: (id) => ({
				url: `/candidates/${id}/`,
			}),
		}),
		askQuestion: build.mutation<void, AskQuestionReq>({
			query: (body) => ({
				method: 'POST',
				url: '/clarifications/initiate/',
				body,
			}),
			invalidatesTags: ['Questions'],
		}),
		getQuestions: build.query<
			ClarificationRes,
			{ page: number; page_size: number }
		>({
			query: (params) => ({
				url: '/clarifications/notifications/',
				params,
			}),
			providesTags: ['Questions'],
		}),
		getQuestionDetails: build.query<ClarificationDetailRes, string>({
			query: (id) => ({
				url: `/clarifications/${id}/status/`,
			}),
			providesTags: ['Questions'],
		}),

	}),
})

export const {
	useLoginMutation,
	useGetVacanciesQuery,
	useGetVacancyQuery,
	usePostVacancyMutation,
	useParseVacancyMutation,
	usePutVacancyMutation,
	useSearchCandidatesMutation,
	useGetMatchedCandidatesQuery,
	useGetCandidateQuery,
	useAskQuestionMutation,
	useGetQuestionsQuery,
	useGetQuestionDetailsQuery,
	useDeleteVacancyMutation,
} = vacanciesApi
